// panelConnection — is the board still telling the truth?
//
// Split out of controlPanel.js, which had grown past the repo's 500-line
// ceiling. This is the panel's safety state machine and it stands on its own:
// nothing here knows about lanes, breaks or staffing.
//
// Pure. The clock always arrives as an argument, so a test can stand at any
// moment without mocking time.

// ── Is this board still live? ────────────────────────────────────────────────

// Thresholds in milliseconds. Named because the numbers are a judgement, not a
// constant of nature, and the next person deserves to see the judgement.
export const RECONNECT_SOFT_MS = 60_000;   // brief blip; say so quietly
export const NOT_LIVE_MS = 180_000;        // stop trusting the board

// The single answer to "is what I am looking at true right now?"
//
// ⚠️ THIS IS THE SAFETY PROPERTY OF THE WHOLE PANEL.
// Offline persistence is on, so a reload with no network renders the entire
// board out of IndexedDB and looks completely normal. Nothing about a stale
// board looks stale. Without this, a wall shows forty-minute-old staffing with
// no indication whatsoever — and staffing that is wrong is worse than staffing
// that is missing, because people act on it.
//
// TWO INDEPENDENT SOURCES, deliberately:
//   1. the canary listener, which reports `fromCache` transitions, and
//   2. wall-clock age since the last confirmed server contact.
// The clock is the belt to the canary's braces: if no callback ever arrives,
// for any reason at all, the age still advances. A staleness detector that can
// only be triggered by an event is useless against the failure where events
// stop arriving.
//
// It also deliberately does NOT ramp on "time since the last data change". A
// healthy, quiet OR board legitimately produces no writes for twenty minutes;
// treating silence as failure would turn the whole wall red at lunchtime, and
// within a week nobody would look at red again.
export function connectionLevel(online, staleMs) {
  const age = Number.isFinite(staleMs) ? Math.max(0, staleMs) : null;

  // Never contacted the server at all this mount. Not yet a lie — but it is
  // also not confirmed live, so it must not claim to be.
  if (age === null) return online ? "connecting" : "not-live";

  if (online && age < RECONNECT_SOFT_MS) return "live";
  if (age < RECONNECT_SOFT_MS) return "reconnecting";
  if (age < NOT_LIVE_MS) return "reconnecting-late";
  return "not-live";
}

// mm:ss for the reconnect countdown; whole minutes once it stops being a blip.
export function staleLabel(staleMs) {
  if (!Number.isFinite(staleMs) || staleMs < 0) return "";
  const totalSec = Math.floor(staleMs / 1000);
  if (totalSec < 3600) {
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  return `${h}h ${m}m`;
}

// What the rail says, and whether the board must carry the full-width band.
//
// The band steals lane space on purpose. A board that cannot tell you it is
// lying is worse than a board that shows you one fewer room.
export function connectionState(online, staleMs, lastContactAt, nowMs) {
  const level = connectionLevel(online, staleMs);
  const clock =
    lastContactAt instanceof Date
      ? lastContactAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
      : null;

  switch (level) {
    case "live":
      return { level, glyph: "●", rail: "LIVE", band: null, tone: "ok" };
    case "connecting":
      return { level, glyph: "○", rail: "CONNECTING…", band: null, tone: "warn" };
    case "reconnecting":
      return { level, glyph: "●", rail: `RECONNECTING · ${staleLabel(staleMs)}`, band: null, tone: "warn" };
    case "reconnecting-late":
      return { level, glyph: "▲", rail: `RECONNECTING · ${staleLabel(staleMs)}`, band: null, tone: "bad" };
    default:
      return {
        level: "not-live",
        glyph: "▲",
        rail: clock ? `NOT LIVE · LAST UPDATE ${clock}` : "NOT LIVE · NEVER CONNECTED",
        // Says the time, the age, AND the consequence. "Offline" alone leaves
        // the reader to work out whether they can still trust the board.
        band: clock
          ? `NOT LIVE — this board last updated at ${clock} (${staleLabel(staleMs)} ago). Staffing shown may be wrong.`
          : "NOT LIVE — this board has never reached the server. Staffing shown may be wrong.",
        tone: "bad",
      };
  }
}
